import "./Panel.css";
import {
	type Accessor,
	createEffect,
	createSignal,
	type JSX,
	onCleanup,
	type ParentComponent,
	Show,
} from "solid-js";
import { Portal } from "solid-js/web";
import { nanoID } from "../utils/nanoID";

const FOCUSABLE_SELECTOR =
	'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function getFocusableElements(container: HTMLElement): HTMLElement[] {
	const nodes = container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
	return Array.from(nodes).filter((el) => {
		if (el.getAttribute("aria-hidden") === "true") return false;
		return el.offsetParent !== null;
	});
}

type PanelSide = "left" | "right" | "top" | "bottom";

/** Ref-count so nested / multiple panels don't unlock scroll prematurely */
let bodyScrollLockDepth = 0;
let lockedScrollY = 0;
let savedBodyPosition = "";
let savedBodyTop = "";
let savedBodyLeft = "";
let savedBodyRight = "";
let savedBodyWidth = "";

/**
 * Freeze document scroll without `overflow: hidden` on `html`/`body`.
 * That pattern changes the scrollport and breaks `position: sticky` sidebars
 * (e.g. `aside.side-bar` with `min-height: 100vh`).
 */
function acquireBodyScrollLock() {
	if (bodyScrollLockDepth === 0) {
		lockedScrollY =
			window.scrollY ||
			window.pageYOffset ||
			document.documentElement.scrollTop;
		const body = document.body;
		savedBodyPosition = body.style.position;
		savedBodyTop = body.style.top;
		savedBodyLeft = body.style.left;
		savedBodyRight = body.style.right;
		savedBodyWidth = body.style.width;

		body.style.position = "fixed";
		body.style.top = `-${lockedScrollY}px`;
		body.style.left = "0";
		body.style.right = "0";
		body.style.width = "100%";
	}
	bodyScrollLockDepth++;
}

function releaseBodyScrollLock() {
	bodyScrollLockDepth = Math.max(0, bodyScrollLockDepth - 1);
	if (bodyScrollLockDepth === 0) {
		const body = document.body;
		body.style.position = savedBodyPosition;
		body.style.top = savedBodyTop;
		body.style.left = savedBodyLeft;
		body.style.right = savedBodyRight;
		body.style.width = savedBodyWidth;
		window.scrollTo({ top: lockedScrollY, left: 0, behavior: "instant" });
	}
}

export type Props = {
	isOpen: Accessor<boolean>;
	onToggle: () => void;
	title?: string;
	footer?: JSX.Element;
	width?: string;
	side?: PanelSide;
	class?: string;
	titleContent?: JSX.Element;
	theme?: "white" | "purple950";
	symbol?: string;
};

export const BasePanel: ParentComponent<Props> = (props) => {
	const [isAnimating, setIsAnimating] = createSignal(false);
	const [shouldRender, setShouldRender] = createSignal(false);
	const id = nanoID("gvtui-panel", 10);
	let previousActiveElement: HTMLElement | null = null;
	let panelRef: HTMLElement | null = null;
	// Track whether the pointer went down inside the panel so a drag-out doesn't
	// dismiss the overlay on mouseup/click.
	let mouseDownedInsidePanel = false;

	// Helper to get isOpen value (handles both boolean and accessor function)
	const getIsOpen = () => props.isOpen();

	/** Same as the header close button: one close action, never while already closed. */
	const closeFromUser = () => {
		if (!getIsOpen()) {
			return;
		}
		props.onToggle();
	};

	// Handle animation states, focus capture and restore
	createEffect(() => {
		const open = getIsOpen();
		if (open) {
			previousActiveElement = document.activeElement as HTMLElement | null;
			// Render first, then trigger animation
			setShouldRender(true);
			// Use requestAnimationFrame to ensure DOM is ready before animating
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					setIsAnimating(true);
					// Move focus into panel once ref is set (aside has mounted)
					requestAnimationFrame(() => {
						if (panelRef) {
							const focusable = getFocusableElements(panelRef);
							if (focusable.length > 0) {
								focusable[0].focus();
							} else {
								panelRef.focus();
							}
						}
					});
				});
			});
		} else {
			// Start exit animation
			setIsAnimating(false);
			panelRef = null;
			previousActiveElement?.focus?.();
			// Remove from DOM after animation completes
			const timeoutId = setTimeout(() => {
				setShouldRender(false);
			}, 300); // Match CSS animation duration

			return () => clearTimeout(timeoutId);
		}
	});

	// Lock scroll only while the panel is open. Do not tie this to
	// `shouldRender()` — the overlay stays mounted for the exit animation after
	// `isOpen` goes false, and a fixed `body` during that window breaks clicks.
	createEffect(() => {
		if (!getIsOpen()) return;
		acquireBodyScrollLock();
		onCleanup(() => releaseBodyScrollLock());
	});

	// Escape closes once (capture phase), matching the header X — no duplicate
	// handlers on the overlay/aside, which would call onToggle again on toggle-based parents.
	createEffect(() => {
		if (!getIsOpen()) return;
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				if (!getIsOpen()) {
					return;
				}
				e.stopImmediatePropagation();
				closeFromUser();
				return;
			}
			if (e.key !== "Tab") return;
			if (!panelRef) return;
			const panel = panelRef;
			const focusable = getFocusableElements(panel);
			if (focusable.length === 0) return;
			const current = document.activeElement as HTMLElement | null;
			const currentIndex = current ? focusable.indexOf(current) : -1;
			if (e.shiftKey) {
				if (currentIndex <= 0 || !panel.contains(current)) {
					e.preventDefault();
					focusable[focusable.length - 1].focus();
				}
			} else {
				if (currentIndex === focusable.length - 1 || !panel.contains(current)) {
					e.preventDefault();
					focusable[0].focus();
				}
			}
		};
		window.addEventListener("keydown", onKeyDown, true);
		return () => window.removeEventListener("keydown", onKeyDown, true);
	});

	const side = () => props.side ?? "right";
	const size = () =>
		props.width
			? {
					width:
						side() === "left" || side() === "right" ? props.width : "100%",
					height:
						side() === "top" || side() === "bottom" ? props.width : "100%",
				}
			: {};

	return (
		<Show when={shouldRender()}>
			<Portal>
				<div
					id={id}
					classList={{
						"gvtui-panel-overlay": true,
						"gvtui-panel-overlay--open": isAnimating(),
						[`gvtui-panel-overlay--${side()}`]: true,
						[props.class ?? ""]: props.class !== undefined,
					}}
					onMouseDown={(e) => {
						mouseDownedInsidePanel =
							panelRef?.contains(e.target as Node) ?? false;
					}}
					onClick={(e) => {
						// Only treat a direct hit on the backdrop as dismiss (not children).
						// e.target === e.currentTarget is reliable; id/class checks can miss
						// when the hit target is not the overlay element itself.
						// Also ignore clicks that originated inside the panel — the user may
						// have dragged out before releasing (mousedown-inside + mouseup-on-backdrop).
						if (e.target === e.currentTarget && !mouseDownedInsidePanel) {
							// Avoid ghost clicks: closing can drop pointer-events before mouseup,
							// so the subsequent click can land on content under the overlay.
							e.preventDefault();
							e.stopPropagation();
							closeFromUser();
						}
						mouseDownedInsidePanel = false;
					}}
					onKeyUp={(e) => {
						if (e.key === "Enter" || e.key === " ") {
							if (
								e.target.id === id ||
								(e.target as HTMLElement).classList.contains(
									"gvtui-panel-overlay",
								)
							) {
								e.preventDefault();
								closeFromUser();
							}
						}
					}}
					role="dialog"
					aria-modal="true"
					aria-labelledby={`${id}-title`}
				>
					<aside
						ref={panelRef}
						class="gvtui-panel panel"
						classList={{
							[`gvtui-panel--${props.theme}`]: props.theme === "purple950",
							[`gvtui-panel--${side()}`]: true,
							"gvtui-panel--open": isAnimating(),
						}}
						style={{ overflow: "auto", ...size() }}
						tabIndex={-1}
						onClick={(e) => e.stopPropagation()}
					>
						{props.children}
					</aside>
				</div>
			</Portal>
		</Show>
	);
};
